import { createFileRoute } from "@tanstack/react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useState } from "react";
import { getSlaSummary } from "@/lib/sla.functions";
import {
  listIncidents,
  createIncident,
  addIncidentUpdate,
  listSystemComponents,
  upsertSystemComponent,
} from "@/lib/incidents.functions";
import { useScope } from "@/hooks/use-scope";
import { PageHeader } from "@/components/ui/page-header";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { CheckCircle2, AlertTriangle, Activity, Timer, Gauge, CalendarClock, Plus, MessageSquarePlus } from "lucide-react";
import { toast } from "sonner";

export const Route = createFileRoute("/_authenticated/app/admin/status")({ component: StatusAdmin });

const SLOS: Array<{ key: string; label: string; target: number; unit: string; icon: any; higherIsBetter?: boolean }> = [
  { key: "p95_pick_action_ms", label: "P95 pick action", target: 500, unit: "ms", icon: Activity },
  { key: "p95_label_buy_ms", label: "P95 label buy", target: 3000, unit: "ms", icon: Timer },
  { key: "p95_dashboard_ms", label: "P95 dashboard", target: 1500, unit: "ms", icon: Gauge },
  { key: "on_time_ship_pct", label: "On-time ship rate", target: 98, unit: "%", icon: CalendarClock, higherIsBetter: true },
];

function StatusAdmin() {
  const { tenantId } = useScope();
  const qc = useQueryClient();
  const sla = useServerFn(getSlaSummary);
  const listInc = useServerFn(listIncidents);
  const createInc = useServerFn(createIncident);
  const addUpd = useServerFn(addIncidentUpdate);
  const listComp = useServerFn(listSystemComponents);
  const upsertComp = useServerFn(upsertSystemComponent);

  const { data: summary } = useQuery({
    queryKey: ["status-sla", tenantId],
    enabled: !!tenantId,
    queryFn: () => sla({ data: { tenantId: tenantId! } }),
    refetchInterval: 30_000,
  });
  const { data: incidents } = useQuery({
    queryKey: ["status-incidents", tenantId],
    enabled: !!tenantId,
    queryFn: () => listInc({ data: { tenantId: tenantId!, limit: 25 } }),
  });
  const { data: components } = useQuery({
    queryKey: ["status-components", tenantId],
    enabled: !!tenantId,
    queryFn: () => listComp({ data: { tenantId: tenantId! } }),
  });

  const createIncMut = useMutation({
    mutationFn: (vars: any) => createInc({ data: { ...vars, tenantId: tenantId! } }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["status-incidents", tenantId] });
      toast.success("Incident created");
    },
    onError: (e: any) => toast.error(e?.message ?? "Failed"),
  });
  const updateIncMut = useMutation({
    mutationFn: (vars: any) => addUpd({ data: { ...vars, tenantId: tenantId! } }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["status-incidents", tenantId] });
      toast.success("Update posted");
    },
    onError: (e: any) => toast.error(e?.message ?? "Failed"),
  });
  const upsertCompMut = useMutation({
    mutationFn: (vars: any) => upsertComp({ data: { ...vars, tenantId: tenantId! } }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["status-components", tenantId] });
      toast.success("Component saved");
    },
    onError: (e: any) => toast.error(e?.message ?? "Failed"),
  });

  return (
    <>
      <PageHeader
        title="Status & SLOs"
        description="SLOs, components, and customer-visible incidents."
      />

      <div className="px-4 sm:px-6 py-4 space-y-4">
        <Card>
          <CardHeader><CardTitle className="text-base">Service Level Objectives</CardTitle></CardHeader>
          <CardContent className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {SLOS.map((s) => {
              const Icon = s.icon;
              const cur = Number(summary?.[s.key] ?? 0);
              const ok = s.higherIsBetter ? cur >= s.target : cur > 0 && cur <= s.target;
              return (
                <div key={s.key} className="rounded-lg border p-3">
                  <div className="flex items-center justify-between text-xs text-muted-foreground">
                    <span className="flex items-center gap-1.5"><Icon className="h-3.5 w-3.5" />{s.label}</span>
                    {cur === 0 ? (
                      <Badge variant="outline" className="text-[10px]">no data</Badge>
                    ) : ok ? (
                      <Badge className="bg-success/15 text-success text-[10px]">
                        <CheckCircle2 className="h-3 w-3 mr-0.5" /> on target
                      </Badge>
                    ) : (
                      <Badge className="bg-warning/15 text-warning text-[10px]">
                        <AlertTriangle className="h-3 w-3 mr-0.5" /> over budget
                      </Badge>
                    )}
                  </div>
                  <div className="mt-2 text-2xl font-semibold tabular-nums">
                    {cur === 0 ? "—" : Math.round(cur).toLocaleString()}
                    <span className="text-xs text-muted-foreground ml-1">{s.unit}</span>
                  </div>
                  <div className="text-[11px] text-muted-foreground mt-1">
                    Target {s.higherIsBetter ? "≥" : "≤"} {s.target}{s.unit}
                  </div>
                </div>
              );
            })}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex-row items-center justify-between">
            <CardTitle className="text-base">Components</CardTitle>
            <ComponentDialog onSubmit={(v) => upsertCompMut.mutate(v)} />
          </CardHeader>
          <CardContent className="divide-y">
            {(components ?? []).length === 0 && (
              <p className="text-sm text-muted-foreground py-2">No components yet — add one to power the public status page.</p>
            )}
            {(components ?? []).map((c: any) => (
              <div key={c.id} className="flex items-center justify-between py-2 gap-2">
                <div className="min-w-0">
                  <div className="font-medium text-sm truncate">{c.name} <span className="text-muted-foreground text-xs">/{c.slug}</span></div>
                  {c.description && <div className="text-xs text-muted-foreground truncate">{c.description}</div>}
                </div>
                <Select
                  value={c.current_state}
                  onValueChange={(v) => upsertCompMut.mutate({
                    id: c.id, name: c.name, slug: c.slug, description: c.description ?? undefined,
                    currentState: v, displayOrder: c.display_order,
                  })}
                >
                  <SelectTrigger className="w-[180px] h-8"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="operational">Operational</SelectItem>
                    <SelectItem value="degraded">Degraded</SelectItem>
                    <SelectItem value="partial_outage">Partial outage</SelectItem>
                    <SelectItem value="major_outage">Major outage</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            ))}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex-row items-center justify-between">
            <CardTitle className="text-base">Incidents</CardTitle>
            <IncidentDialog onSubmit={(v) => createIncMut.mutate(v)} />
          </CardHeader>
          <CardContent className="space-y-3">
            {(incidents ?? []).length === 0 && (
              <p className="text-sm text-muted-foreground">No incidents recorded.</p>
            )}
            {(incidents ?? []).map((i: any) => (
              <div key={i.id} className="border rounded-md p-3 space-y-2">
                <div className="flex items-center justify-between gap-2">
                  <div>
                    <div className="font-medium text-sm">{i.title}</div>
                    <div className="text-[11px] text-muted-foreground">
                      Started {new Date(i.started_at).toLocaleString()}
                      {i.resolved_at && ` · Resolved ${new Date(i.resolved_at).toLocaleString()}`}
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    <Badge variant="outline" className="text-[10px] uppercase">{i.severity}</Badge>
                    <Badge variant="outline" className="text-[10px]">{i.state}</Badge>
                    {i.customer_visible && <Badge className="bg-blue-500/15 text-blue-700 dark:text-blue-400 text-[10px]">public</Badge>}
                  </div>
                </div>
                {i.state !== "resolved" && (
                  <UpdateForm onSubmit={(v) => updateIncMut.mutate({ id: i.id, ...v })} />
                )}
              </div>
            ))}
          </CardContent>
        </Card>

        <div className="grid gap-4 lg:grid-cols-2">
          <Card>
            <CardHeader><CardTitle className="text-base">Today at a Glance</CardTitle></CardHeader>
            <CardContent className="space-y-2 text-sm">
              <Row label="Open orders" value={summary?.open_orders} />
              <Row label="Due today" value={summary?.due_today} />
              <Row label="Shipped today" value={summary?.shipped_today} />
              <Row label="Pick rate (per hr)" value={summary?.pick_rate_per_hr} />
              <Row label="Dock-to-stock (hrs, 7d)" value={summary?.dock_to_stock_hours} />
            </CardContent>
          </Card>

          <Card>
            <CardHeader><CardTitle className="text-base">On-Call & Incident Response</CardTitle></CardHeader>
            <CardContent className="space-y-3 text-sm">
              <div>
                <div className="text-xs text-muted-foreground mb-1">Primary on-call rotation</div>
                <div className="font-mono text-xs bg-muted/40 p-2 rounded">PagerDuty · ops-primary (1wk rotation)</div>
              </div>
              <div>
                <div className="text-xs text-muted-foreground mb-1">Runbooks</div>
                <ul className="text-xs space-y-1 list-disc pl-4">
                  <li>docs/runbooks/breach-playbook.md — security incident</li>
                  <li>docs/runbooks/restore-drill.md — DB restore</li>
                  <li>docs/runbooks/soc2-readiness.md — compliance evidence</li>
                  <li>docs/runbooks/tablet-qa-checklist.md — pre-deploy QA</li>
                </ul>
              </div>
              <div>
                <div className="text-xs text-muted-foreground mb-1">SLA targets (availability)</div>
                <div className="text-xs">API 99.9% · Dashboard 99.5% · Label buy 99.5%</div>
                {tenantId && (
                  <div className="text-[11px] text-muted-foreground mt-2">
                    Public status page: <code>/status?tenant={tenantId}</code>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </>
  );
}

function Row({ label, value }: { label: string; value: number | null | undefined }) {
  return (
    <div className="flex items-center justify-between border-b last:border-0 py-1.5">
      <span className="text-muted-foreground">{label}</span>
      <span className="font-medium tabular-nums">{value == null ? "—" : Math.round(Number(value)).toLocaleString()}</span>
    </div>
  );
}

function ComponentDialog({ onSubmit }: { onSubmit: (v: any) => void }) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [slug, setSlug] = useState("");
  const [description, setDescription] = useState("");
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" variant="outline"><Plus className="h-3.5 w-3.5 mr-1" />Add component</Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle>New system component</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label className="text-xs">Name</Label><Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Order API" /></div>
          <div><Label className="text-xs">Slug</Label><Input value={slug} onChange={(e) => setSlug(e.target.value.toLowerCase().replace(/[^a-z0-9_-]/g, "-"))} placeholder="order-api" /></div>
          <div><Label className="text-xs">Description</Label><Input value={description} onChange={(e) => setDescription(e.target.value)} /></div>
        </div>
        <DialogFooter>
          <Button onClick={() => {
            if (!name || !slug) return;
            onSubmit({ name, slug, description: description || undefined, currentState: "operational", displayOrder: 0 });
            setOpen(false); setName(""); setSlug(""); setDescription("");
          }}>Create</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function IncidentDialog({ onSubmit }: { onSubmit: (v: any) => void }) {
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState("");
  const [severity, setSeverity] = useState<"minor" | "major" | "critical">("minor");
  const [message, setMessage] = useState("");
  const [customerVisible, setCustomerVisible] = useState(true);
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm"><Plus className="h-3.5 w-3.5 mr-1" />New incident</Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle>New incident</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label className="text-xs">Title</Label><Input value={title} onChange={(e) => setTitle(e.target.value)} /></div>
          <div><Label className="text-xs">Severity</Label>
            <Select value={severity} onValueChange={(v: any) => setSeverity(v)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="minor">Minor</SelectItem>
                <SelectItem value="major">Major</SelectItem>
                <SelectItem value="critical">Critical</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div><Label className="text-xs">Initial message</Label><Textarea value={message} onChange={(e) => setMessage(e.target.value)} rows={3} /></div>
          <label className="flex items-center gap-2 text-xs">
            <input type="checkbox" checked={customerVisible} onChange={(e) => setCustomerVisible(e.target.checked)} />
            Show on public status page
          </label>
        </div>
        <DialogFooter>
          <Button onClick={() => {
            if (!title || !message) return;
            onSubmit({ title, severity, initialMessage: message, customerVisible, affectedComponents: [] });
            setOpen(false); setTitle(""); setMessage(""); setSeverity("minor");
          }}>Create</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function UpdateForm({ onSubmit }: { onSubmit: (v: { state: string; message: string }) => void }) {
  const [state, setState] = useState<"investigating" | "identified" | "monitoring" | "resolved">("investigating");
  const [message, setMessage] = useState("");
  return (
    <div className="flex flex-col sm:flex-row gap-2 pt-2 border-t">
      <Select value={state} onValueChange={(v: any) => setState(v)}>
        <SelectTrigger className="w-[150px] h-8"><SelectValue /></SelectTrigger>
        <SelectContent>
          <SelectItem value="investigating">Investigating</SelectItem>
          <SelectItem value="identified">Identified</SelectItem>
          <SelectItem value="monitoring">Monitoring</SelectItem>
          <SelectItem value="resolved">Resolved</SelectItem>
        </SelectContent>
      </Select>
      <Input value={message} onChange={(e) => setMessage(e.target.value)} placeholder="Status update…" className="h-8" />
      <Button size="sm" variant="outline" onClick={() => {
        if (!message) return;
        onSubmit({ state, message });
        setMessage("");
      }}><MessageSquarePlus className="h-3.5 w-3.5 mr-1" />Post</Button>
    </div>
  );
}
